import GeoLocationError from "./error/GeoLocationError";
import LocalizeByGPSError from "./error/LocalizeByGPSError";
import LocalizeByIpError from "./error/LocalizeByIpError";
import LocalizeByStoreError from "./error/LocalizeByStoreError";
import LocalizeByZipCodeError from "./error/LocalizeByZipCodeError";
import NoStoreFoundError from "./error/NoStoreFoundError";
import deliveryZipcodeService from "./deliveryZipcodeService";
import cookie from "./storageManager/methods/cookie";
import http from "./storageManager/methods/http";
import local from "./storageManager/methods/local";
import localizationServiceProxy from "./localizationServiceProxy";
import storeService from "./storeService";
import userService from "./userService";
import { EventEmitter } from "./eventEmitter/eventEmitter";
import {
  calculateExpirationTime,
  isValidZipCode,
  normalizeZipCode,
  normalizeStoreId,
  shouldUpdateZipCode,
} from "./common/utils";
import {
  getThdLocalizer,
  setThdLocalizer,
} from "./storageManager/storageManager";
import {
  ACCURACY_THRESHOLD_HIGH,
  ACCURACY_THRESHOLD_LOW,
  CATEGORIES,
  DEFAULT_STORE,
  EVENTS,
  EXTERNAL_USERS,
  INTERNAL_USERS,
  LIFECYCLE_EVENTS,
  MESSAGES,
  RADIUS,
  STORAGE_IDENTIFIERS,
  TIMEOUT,
  TIMESPANS,
  WORKFLOW_MAP,
  WORKFLOWS,
} from "./common/constants";

class PubSub extends EventEmitter {}
export const LocalizerEventEmitter = new PubSub();

export const localizerResponse = {
  status: userService.status,
  getLocalizedStore: () => storeService.getStore(),
  getUserStore: () => storeService.getUserStore(),
};

let isLocalizedByGoogle = false;
let softLinkRadius = RADIUS.SOFT_LINK_MIN_MILE_RADIUS;
// Promise for in-progress "IP + GPS" auto-localization run
let autoLocalizationPromise = null;
let localStore = null;

const localizerService = {
  buildStoreIdRequest(request) {
    const storeId = Number(
      localizerService.getQueryParameterByName("store") ||
        localizerService.getQueryParameterByName("g_store") ||
        localizerService.getNativeLocalizedStore()
    );
    if (storeId) {
      return { storeId };
    }
    if (request?.storeId) {
      return { ...request, storeId: Number(request.storeId) };
    }
  },
  checkAuthorizedGeoLocation() {
    const authorizedGeoLocation = local.get(
      STORAGE_IDENTIFIERS.AUTHORIZED_GEOLOCATION
    );
    return !!(
      authorizedGeoLocation &&
      authorizedGeoLocation !== CATEGORIES.AUTHORIZED_GEOLOCATION.UNAUTHORIZED
    );
  },
  async checkForStoreNumberForUserInsideStore(position) {
    try {
      const response = await localizationServiceProxy.getNearestStoreByRadius(
        position?.coords
      );
      return response?.stores?.[0]?.storeId || "Not In Store";
    } catch (error) {
      throw new Error(error);
    }
  },
  createThdLocalizer(localStore, workflow) {
    try {
      const fixedStoreId = localStore.storeId.replace(/^0/, "");
      const storeDetails =
        fixedStoreId +
        "+" +
        localStore.name +
        " - " +
        (localStore.address && localStore.address.city) +
        ", " +
        (localStore.address && localStore.address.state) +
        "+";

      const entries = {
        [STORAGE_IDENTIFIERS.THD_LOCALIZER.WORKFLOW]: workflow,
        [STORAGE_IDENTIFIERS.THD_LOCALIZER.THD_FORCE_LOC]:
          WORKFLOW_MAP[workflow]?.category,
        [STORAGE_IDENTIFIERS.THD_LOCALIZER.THD_LOCSTORE]: storeDetails,
        [STORAGE_IDENTIFIERS.THD_LOCALIZER.THD_STRFINDERZIP]:
          localStore.address && localStore.address.postalCode,
        [STORAGE_IDENTIFIERS.THD_LOCALIZER.THD_STORE_HOURS]:
          storeService.mapStoreHoursToString(localStore.storeHours),
        [STORAGE_IDENTIFIERS.THD_LOCALIZER.THD_STORE_HOURS_EXPIRY]:
          calculateExpirationTime(TIMESPANS.ONE_HOUR),
      };
      const { THD_INTERNAL } = getThdLocalizer() || {};
      if (
        INTERNAL_USERS.includes(workflow) ||
        THD_INTERNAL === CATEGORIES.THD_INTERNAL.INTERAL
      ) {
        entries[STORAGE_IDENTIFIERS.THD_LOCALIZER.THD_INTERNAL] =
          CATEGORIES.THD_INTERNAL.INTERAL;
      } else if (
        EXTERNAL_USERS.includes(workflow) ||
        THD_INTERNAL === CATEGORIES.THD_INTERNAL.EXTERNAL
      ) {
        entries[STORAGE_IDENTIFIERS.THD_LOCALIZER.THD_INTERNAL] =
          CATEGORIES.THD_INTERNAL.EXTERNAL;
      }
      setThdLocalizer({ entries, fromScratch: true });
    } catch (e) {
      console.error("Error setting entries: " + e);
    }
  },
  determineGpsWorkflow(position) {
    if (position.coords.accuracy <= ACCURACY_THRESHOLD_LOW) {
      return WORKFLOWS.LOCALIZED_BY_GPS_HIGH;
    } else if (
      localizerService.isAccuracyBetweenThreshold(position) &&
      localizerService.isCurrentAccuracyMediumOrLow()
    ) {
      return WORKFLOWS.LOCALIZED_BY_GPS_MEDIUM;
    } else if (userService.status.getAccuracy() === "LOW") {
      return WORKFLOWS.LOCALIZED_BY_GPS_LOW;
    }
  },
  async getCurrentLocation() {
    if (navigator?.geolocation) {
      return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          resolve,
          (error) => {
            reject(new GeoLocationError(error));
          },
          {
            timeout: TIMEOUT,
          }
        );
      });
    } else {
      throw new GeoLocationError({
        message: "navigator.geolocation is not supported",
      });
    }
  },
  getNativeLocalizedStore() {
    return cookie.get(STORAGE_IDENTIFIERS.NATIVE_LOCALIZED_STORE);
  },
  getQueryParameterByName(name) {
    const urlParams = new URLSearchParams(window?.location?.search);
    return urlParams.get(name);
  },
  handleGoogleStoreParameter() {
    try {
      const hasGoogleStore =
        !!localizerService.getQueryParameterByName("g_store");
      if (hasGoogleStore) {
        isLocalizedByGoogle = true;
        const currentUrl = window?.location?.href;
        const updatedUrl = http.removeUrlParam(currentUrl, "g_store");
        history.replaceState(null, null, updatedUrl);
      }
    } catch (err) {
      console.error("Localization error removing g_store from url: ", err);
    }
  },
  handleGpsError(error) {
    let errorEvent;
    let gpsStatusMessage = error.message;
    switch (error.code) {
      case 1:
        errorEvent = EVENTS.USER_DENIED_GPS;
        gpsStatusMessage = MESSAGES.USER_DENIED_GPS;
        break;
      case 3:
        errorEvent = EVENTS.GPS_TIMEOUT;
        gpsStatusMessage = MESSAGES.GPS_TIMEOUT;
        break;
      default:
        errorEvent = EVENTS.GPS_ERROR;
        gpsStatusMessage = MESSAGES.GPS_ERROR;
    }
    userService.operations.setGpsStatus(gpsStatusMessage);
    localizerService.publish(errorEvent);
  },
  isAccuracyBetweenThreshold(position) {
    return (
      position.coords.accuracy > ACCURACY_THRESHOLD_LOW &&
      position.coords.accuracy < ACCURACY_THRESHOLD_HIGH
    );
  },
  isCanadianStore(localStore) {
    return localStore?.address?.country === "CA";
  },
  isCurrentAccuracyMediumOrLow() {
    const accuracy = userService.status.getAccuracy();
    return (
      accuracy === undefined || accuracy === "LOW" || accuracy === "MEDIUM"
    );
  },
  async localize(params) {
    try {
      const request = localizerService.buildStoreIdRequest(params);
      const currentStoreId = Number(
        localizerResponse.getLocalizedStore()?.storeId
      );
      if (request && request?.storeId !== currentStoreId) {
        await localizerService.localizeByStore(request);
      } else if (userService.status.isLocalized()) {
        await localizerService.updateAlreadyLocalizedUser();
      } else {
        if (!autoLocalizationPromise) {
          autoLocalizationPromise =
            localizerService.localizeAutoWithFallbacks();
        }
        await autoLocalizationPromise;
      }
    } catch (e) {
      console.error("Error localizing: ", e);
      if (e instanceof LocalizeByStoreError) {
        throw localizerResponse;
      }
    } finally {
      if (localizerService.isCanadianStore(localStore)) {
        userService.operations.markAsCanadianUser();
      }
    }
    return localizerResponse;
  },
  async localizeAutoWithFallbacks() {
    try {
      await localizerService.localizeByIp();
      const workflow = userService.status.getWorkflow();
      if (workflow !== WORKFLOWS.THD_STORE_LOCATION) {
        await localizerService.localizeByGPS();
      }
    } catch (e) {
      try {
        if (e instanceof LocalizeByIpError) {
          await localizerService.localizeByGPS();
        } else if (e instanceof LocalizeByGPSError) {
          localizerService.localizeDefault();
        }
      } catch (e) {
        if (e instanceof LocalizeByGPSError) {
          localizerService.localizeDefault();
        }
      }
    }
  },
  async localizeByGPS() {
    if (userService.status.isRequestFromConsumerApp()) {
      throw new LocalizeByGPSError("GPS localization skipped for consumer app");
    }

    try {
      const position = await localizerService.getCurrentLocation();
      userService.operations.setGpsStatus("gps access granted");

      const httpResponse = await localizationServiceProxy.getLocalStore(
        position?.coords
      );
      localStore = httpResponse?.localStore;

      if (localizerService.isCanadianStore(localStore)) {
        throw new LocalizeByGPSError(
          "Canadian store detected, skipping GPS localization"
        );
      }
      localizerService.processResponse({
        localStore,
        localizationEvent: EVENTS.LOCALIZED_BY_GPS,
        workflow: localizerService.determineGpsWorkflow(position),
      });
    } catch (e) {
      if (e instanceof GeoLocationError) {
        localizerService.handleGpsError(e);
      }
      throw new LocalizeByGPSError("GPS localization failed: " + e.message);
    }
  },
  async localizeByIp() {
    try {
      const httpResponse = await localizationServiceProxy.getLocalStore();
      if (!httpResponse?.localStore) {
        throw new Error("No local store found");
      }
      localStore = httpResponse?.localStore;
      if (localizerService.isCanadianStore(localStore)) {
        throw new LocalizeByIpError(
          "Canadian store detected, skipping IP localization"
        );
      }
      localizerService.processResponse({
        localStore,
        localizationEvent: EVENTS.LOCALIZED_BY_IP,
        workflow: httpResponse?.metadata?.workflow,
      });
    } catch (e) {
      localizerService.publish(EVENTS.LOCALIZE_BY_IP_FAILED);
      throw new LocalizeByIpError("Error localizing by IP: " + e.message);
    }
  },
  async localizeByStore(request, workflow) {
    try {
      const httpResponse = await localizationServiceProxy.getStoreFromSSS(
        request?.storeId
      );
      if (httpResponse.stores[0]) {
        localizerService.processResponse({
          localStore: httpResponse.stores[0],
          localizationEvent: EVENTS.LOCALIZED_BY_STORE,
          localizationMethod: request?.localizationMethod,
          workflow: workflow || WORKFLOWS.LOCALIZED_BY_STORE,
        });
      }
    } catch (e) {
      localizerService.publish(EVENTS.LOCALIZE_BY_STORE_FAILED);
      throw new LocalizeByStoreError("Error localizing by store: " + e.message);
    }
  },
  async localizeByZipCode(zipcode) {
    try {
      if (isValidZipCode(zipcode)) {
        const httpResponse = await localizationServiceProxy.getStoreNearby({
          pagesize: 1,
          radius: RADIUS.MAX_POSSIBLE_MILE_RADIUS,
          timeout: TIMEOUT,
          zipcode,
        });
        const localStore = httpResponse?.stores[0];
        if (!localStore) {
          throw new NoStoreFoundError();
        }
        localizerService.processResponse({
          localStore,
          localizationEvent: EVENTS.LOCALIZED_BY_ZIPCODE,
          workflow: WORKFLOWS.LOCALIZED_BY_ZIPCODE,
          skipSoftLinkValidation: true,
          zipcode,
        });
        localizerService.publish(EVENTS.DELIVERY_ZIP_UPDATED, {
          skipSoftLinkValidation: true,
        });
      }
    } catch (e) {
      localizerService.publish(EVENTS.LOCALIZE_BY_ZIPCODE_FAILED);
      throw new LocalizeByZipCodeError(
        "Error localizing by zip code: " + e.message
      );
    }
  },
  localizeDefault() {
    if (!userService.status.isLocalized()) {
      localizerService.processResponse({
        localStore: DEFAULT_STORE,
        localizationEvent: EVENTS.LOCALIZED_BY_DEFAULT,
        workflow: WORKFLOWS.LOCALIZED_BY_DEFAULT,
      });
    }
  },
  localizeFuzzy: async (request) => {
    try {
      if (localizerService.isCurrentAccuracyMediumOrLow()) {
        await localizerService.localizeByStore(
          request,
          WORKFLOWS.LOCALIZED_BY_FUZZY_STORE
        );
      }
    } catch (e) {
      console.error("Error localizing by fuzzy store: ", e);
    }
    return localizerResponse;
  },
  off(event) {
    LocalizerEventEmitter.off(event);
  },
  on(events, eventHandler) {
    LocalizerEventEmitter.on(events, eventHandler);
  },
  processResponse({
    localStore,
    localizationEvent,
    localizationMethod,
    skipSoftLinkValidation,
    workflow,
    zipcode,
  }) {
    userService.operations.markAsInStoreUser(workflow);

    const finalLocalizationMethod =
      localizationMethod ||
      (WORKFLOW_MAP[workflow] ? WORKFLOW_MAP[workflow].alias : workflow);
    local.put({
      key: STORAGE_IDENTIFIERS.LOCALIZATION_METHOD,
      value: finalLocalizationMethod,
    });
    localizerService.createThdLocalizer(localStore, workflow);

    if (shouldUpdateZipCode(zipcode)) {
      deliveryZipcodeService.updateDeliveryZipcodeOnLocalizationWorkflow(
        localStore,
        workflow,
        zipcode
      );
    }

    localizerService.handleGoogleStoreParameter();
    localizerService.publish(EVENTS.LOCALIZED, { skipSoftLinkValidation });
    localizerService.publish(localizationEvent);

    if (typeof window !== "undefined" && window.THD_EVENT_BUS) {
      const localizationOpts = {
        localStore,
        workflow,
        localizationEvent,
        localizationMethod,
      };

      window.THD_EVENT_BUS.lifeCycle.trigger(
        LIFECYCLE_EVENTS.LOCALIZED,
        localizationOpts
      );
    }

    const wasLocalized = userService.status.isLocalized();
    const previouslyLocalizedStore = storeService.getStore();
    const storeIdChanged =
      wasLocalized &&
      Number(previouslyLocalizedStore?.storeId) !== Number(localStore?.storeId);
    if (storeIdChanged) {
      localizerService.publish(EVENTS.LOCALIZED_STORE_CHANGED);
      if (isLocalizedByGoogle) {
        localizerService.publish(EVENTS.LOCALIZED_STORE_CHANGE_GOOGLE);
        isLocalizedByGoogle = false;
      }
    }
  },
  processSoftlinkReponse(response, zipcode, eventName) {
    if (response?.searchReport?.recordCount > 0) {
      const currentStoreId = normalizeStoreId(
        localizerResponse.getLocalizedStore().storeId
      );
      const storeIds = response?.stores?.map((store) => store.storeId);
      const inRange = storeIds?.includes(currentStoreId);
      if (!inRange) {
        localizerService.publish(eventName);
      }
      softLinkRadius = RADIUS.SOFT_LINK_MIN_MILE_RADIUS;
    } else if (softLinkRadius === RADIUS.SOFT_LINK_MIN_MILE_RADIUS) {
      softLinkRadius = RADIUS.SOFT_LINK_MAX_MILE_RADIUS;
      localizerService.validateSoftLink(zipcode, eventName);
    }
  },
  publish(...event) {
    if (event.length === 0) {
      return;
    }
    LocalizerEventEmitter.trigger.apply(LocalizerEventEmitter, event);
  },
  async setStoreHours() {
    try {
      const storeId = localizerResponse.getLocalizedStore().storeId;
      const response = await localizationServiceProxy.getStoreFromSSS(storeId);
      const storeSearchResponse =
        response && response.stores ? response.stores[0] : null;
      if (storeSearchResponse) {
        const storeHours = storeService.mapStoreHoursToString(
          storeSearchResponse?.storeHours
        );
        const storeHoursExpiry = calculateExpirationTime(TIMESPANS.ONE_HOUR);

        setThdLocalizer({
          entries: {
            [STORAGE_IDENTIFIERS.THD_LOCALIZER.THD_STORE_HOURS]: storeHours,
            [STORAGE_IDENTIFIERS.THD_LOCALIZER.THD_STORE_HOURS_EXPIRY]:
              storeHoursExpiry,
          },
        });
      }
    } catch (e) {
      console.error("Error in setting store hours expiry time: " + e);
    }
  },

  async updateAlreadyLocalizedUser() {
    if (shouldUpdateZipCode()) {
      deliveryZipcodeService.updateDeliveryZipcodeFromLocalizerResponse(
        localizerResponse
      );
    }
    if (userService.status.isStoreHoursExpired(localizerResponse)) {
      await localizerService.setStoreHours();
    }
  },
  updateDeliveryZipcodeDetails(deliveryZipCode, type) {
    if (deliveryZipCode && isValidZipCode(normalizeZipCode(deliveryZipCode))) {
      deliveryZipcodeService.updateDeliveryZipcodeDetails(
        deliveryZipCode,
        type
      );
      localizerService.publish(EVENTS.DELIVERY_ZIP_UPDATED);
    }
  },
  updateDeliveryZipcodeDetailsNoEvent(deliveryZipCode, type) {
    if (deliveryZipCode && isValidZipCode(normalizeZipCode(deliveryZipCode))) {
      deliveryZipcodeService.updateDeliveryZipcodeDetails(
        deliveryZipCode,
        type
      );
    }
  },
  async validateSoftLink(zipcode, eventName) {
    if (zipcode) {
      try {
        const response = await localizationServiceProxy.getStoreNearby({
          radius: softLinkRadius,
          zipcode,
        });
        localizerService.processSoftlinkReponse(response, zipcode, eventName);
      } catch (error) {
        console.error("Error validating softlink: ", error);
      }
    }
  },
  // Testing/maintenance helper: clear the in-progress auto-localization promise
  resetAutoLocalizationPromise() {
    autoLocalizationPromise = null;
  },
};

export default localizerService;
